import React from 'react';
import CalculatorCard from '../components/CalculatorCard';
import {
  Car,
  DollarSign,
  Coins,
  Calculator,
  CreditCard,
  Clock,
  Percent,
  Calendar,
  BarChart,
  FileText,
  CheckCircle,
  TrendingDown,
  MapPin,
  Building,
  Shield,
  Lock,
  Users,
  Award,
  BarChart3,
  CheckSquare,
  Receipt,
  Truck,
  Table
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../components/SEOHead';
import carLoanImage from '../assets/car loan payment calculator.jpg';

const CategoryHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="category-title">
    <Icon className="w-7 h-7 text-blue-600" />
    <span>{title}</span>
  </div>
);

const Home = () => {
  const categories = [
    {
      title: "Australian Car Financing Tools",
      icon: Car,
      description: "Essential calculators for Australian car financing and loan planning",
      calculators: [
        {
          title: 'Car Loan Repayment Calculator',
          description: 'Calculate monthly repayments, total interest, and view complete amortization schedule',
          icon: Calculator,
          link: '/car-loan-calculator',
          featured: true
        },
        {
          title: 'Car Loan EMI Calculator',
          description: 'Calculate Equated Monthly Installments (EMI) with detailed amortization and payment breakdown',
          icon: Calculator,
          link: '/car-loan-emi-calculator',
          featured: true
        },
        {
          title: 'Vehicle Stamp Duty Calculator',
          description: 'Calculate stamp duty and transfer fees for all Australian states and territories',
          icon: Receipt,
          link: '/stamp-duty-calculator',
          featured: true
        },
        {
          title: '4WD Loan Calculator',
          description: 'Specialized calculator for 4WD and off-road vehicles including accessories and maintenance costs',
          icon: Truck,
          link: '/4wd-loan-calculator',
          featured: true
        },
        {
          title: 'Luxury Car Tax Calculator',
          description: 'Work out the LCT payable using current ATO thresholds for fuel-efficient and other vehicles',
          icon: Receipt,
          link: '/luxury-car-tax-calculator',
          featured: true
        }
      ]
    },
    {
      title: "Financial Planning",
      icon: DollarSign,
      description: "Plan your vehicle purchase with Australian financing options",
      calculators: [
        {
          title: 'Car Loan Interest Calculator',
          description: 'Calculate total interest paid and compare different interest rates to find the best deal',
          icon: Percent,
          link: '/car-loan-interest-calculator',
          featured: true
        },
        {
          title: 'Car Loan Amortization Calculator',
          description: 'View a detailed payment schedule showing principal and interest breakdown for each payment',
          icon: Table,
          link: '/car-loan-amortization-calculator',
          featured: true
        },
        {
          title: 'Early Payoff Calculator',
          description: 'Calculate savings by paying off your car loan early or making extra payments',
          icon: TrendingDown,
          link: '/early-payoff-calculator',
          featured: true
        },
        {
          title: 'Car Finance Calculator',
          description: 'Estimate your monthly car repayments with current Australian interest rates',
          icon: Calendar,
          link: '/car-loan-calculator',
          featured: false
        },
        {
          title: 'Balloon Payment Calculator',
          description: 'Calculate how a residual payment affects your monthly repayments',
          icon: Percent,
          link: '/car-loan-calculator',
          featured: false
        }
      ]
    },
    {
      title: "Specialized Car Financing",
      icon: CreditCard,
      description: "Specialized calculators for different car financing arrangements in Australia",
      calculators: [
        {
          title: '4WD & Off-Road Financing',
          description: 'Calculate loan repayments for 4WD vehicles including accessories and maintenance costs',
          icon: Truck,
          link: '/4wd-loan-calculator',
          featured: false
        },
        {
          title: 'Novated Lease Calculator',
          description: 'Estimate potential salary sacrifice benefits with a novated lease arrangement',
          icon: FileText,
          link: '/car-loan-calculator',
          featured: false
        },
        {
          title: 'Car Loan Refinance Calculator',
          description: 'See potential savings by refinancing your existing car loan',
          icon: Coins,
          link: '/car-loan-calculator',
          featured: false
        },
        {
          title: 'Car Loan Affordability Calculator',
          description: 'Calculate how much you can afford to borrow based on your budget',
          icon: DollarSign,
          link: '/car-loan-calculator',
          featured: false
        }
      ]
    }
  ];

  return (
    <>
      <SEOHead page="home" />
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Australian Car Loan Repayment Calculator</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Plan your vehicle purchase with our comprehensive car loan calculator designed specifically for Australian car finance options
          </p>
      </div>

        <div className="hero bg-blue-600 text-white rounded-xl p-8 mb-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
              <h2 className="text-3xl font-bold mb-4">Calculate Your Australian Car Loan Repayments</h2>
              <p className="text-lg mb-6">
                Our Australian car loan calculator helps you estimate monthly repayments, total interest costs, and provides a complete amortization schedule based on current Australian interest rates.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Compare repayments with different interest rates from Australian lenders</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Calculate total interest paid over the loan term</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>View detailed monthly repayment schedules</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Understand the impact of balloon payments on your finance</span>
                </li>
              </ul>
              <a 
                href="/car-loan-calculator/" 
                className="inline-block bg-white text-blue-600 font-semibold py-3 px-6 rounded-md hover:bg-blue-50 transition"
              >
                Calculate Your Repayments Now
              </a>
            </div>
            <div className="hidden md:block">
              <div className="relative rounded-lg overflow-hidden">
                <img 
                  src={carLoanImage} 
                  alt="Mazda CX-5 on Australian outback road with kangaroo sign and calculator overlay" 
                  className="w-full h-auto object-cover rounded-lg"
                  style={{ maxHeight: "400px" }}
                />
                <div className="absolute top-0 right-0 bg-transparent text-white p-4 rounded-bl-lg">
                  {/* The bullet points are already in the image */}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-10">Why Use Our Australian Car Loan Calculator?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="rounded-full bg-blue-100 w-16 h-16 flex items-center justify-center mb-4">
                <Percent className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Compare Australian Interest Rates</h3>
              <p className="text-gray-600">
                See how different interest rates from Australian lenders affect your monthly repayments and total loan cost.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="rounded-full bg-blue-100 w-16 h-16 flex items-center justify-center mb-4">
                <Calendar className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Adjust Loan Terms</h3>
              <p className="text-gray-600">
                Explore how different loan durations (1-7 years) impact your financial commitment in the Australian market.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="rounded-full bg-blue-100 w-16 h-16 flex items-center justify-center mb-4">
                <BarChart className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Visualize Repayments</h3>
              <p className="text-gray-600">
                Get detailed charts showing principal and interest payments over time for better financial planning.
              </p>
            </div>
          </div>
        </div>

        {/* Trust Signals Section */}
        <div className="bg-blue-50 p-8 rounded-lg border border-blue-100 mb-16">
          <h2 className="text-2xl font-bold text-center mb-8">Why Trust Our Car Loan Calculator</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-5 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="rounded-full bg-green-100 p-3 mr-4">
                  <BarChart3 className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Financial Accuracy</h3>
                  <p className="text-gray-600 text-sm">
                    Our calculations use the same formulas employed by major Australian banks, updated monthly to reflect current market rates.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-5 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="rounded-full bg-green-100 p-3 mr-4">
                  <CheckSquare className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Verified Data Sources</h3>
                  <p className="text-gray-600 text-sm">
                    Calculator data sourced from Australia's top 15 auto lenders including CommBank, ANZ, Westpac, and NAB.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-5 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="rounded-full bg-green-100 p-3 mr-4">
                  <Shield className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Regulatory Compliance</h3>
                  <p className="text-gray-600 text-sm">
                    Our estimates align with Australian financial services guidelines, while not constituting financial advice.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-5 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="rounded-full bg-green-100 p-3 mr-4">
                  <Award className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Expert Validation</h3>
                  <p className="text-gray-600 text-sm">
                    Calculation methodology verified by certified Australian financial advisors for accuracy and relevance.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-5 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="rounded-full bg-green-100 p-3 mr-4">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Trusted by Australians</h3>
                  <p className="text-gray-600 text-sm">
                    Over 50,000 Australian car buyers used our calculators in 2023 to make informed financing decisions.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-5 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="rounded-full bg-green-100 p-3 mr-4">
                  <Lock className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Your Data Stays Private</h3>
                  <p className="text-gray-600 text-sm">
                    All calculations happen on your device. We never store your financial details or loan information.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <div className="inline-flex items-center justify-center space-x-6 px-6 py-3 bg-white rounded-lg shadow-sm">
              <div className="text-gray-600 font-semibold">Data Sources Include:</div>
              <div className="flex space-x-4">
                <span className="bg-blue-50 px-3 py-1 rounded text-sm text-blue-700">CommBank</span>
                <span className="bg-blue-50 px-3 py-1 rounded text-sm text-blue-700">ANZ</span>
                <span className="bg-blue-50 px-3 py-1 rounded text-sm text-blue-700">Westpac</span>
                <span className="bg-blue-50 px-3 py-1 rounded text-sm text-blue-700">NAB</span>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-3">* Banks mentioned for illustrative purposes only. Not affiliated with or endorsed by these institutions.</p>
          </div>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-md mb-16">
          <h2 className="text-2xl font-bold mb-6">Understanding Car Financing in Australia</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-3 flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                Secured vs. Unsecured Car Loans
              </h3>
              <p className="text-gray-700 mb-6">
                Secured loans use your vehicle as collateral and typically offer lower interest rates (from 4.99% p.a.) 
                than unsecured loans (from 8.99% p.a.). Most Australian car loans are secured to provide better rates.
              </p>
              
              <h3 className="text-xl font-semibold mb-3 flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                Comparison Rates
              </h3>
              <p className="text-gray-700">
                Australian lenders must advertise comparison rates that include fees and charges. 
                Our calculator can help you understand the true cost of loans beyond the headline rate.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-3 flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                Balloon Payments
              </h3>
              <p className="text-gray-700 mb-6">
                Many Australian car loans offer balloon payment options (a lump sum due at the end of the loan term) 
                to reduce regular repayments. Our calculator helps you understand the impact on your finances.
              </p>
              
              <h3 className="text-xl font-semibold mb-3 flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                Typical Loan Terms
              </h3>
              <p className="text-gray-700">
                Most Australian car loans range from 1-7 years, with 5 years being the most common term. 
                Longer terms mean lower monthly repayments but higher total interest costs.
              </p>
            </div>
          </div>
        </div>

        {categories.map((category, idx) => (
          <div key={idx} className="mb-16">
            <CategoryHeader icon={category.icon} title={category.title} />
            <p className="text-gray-600 mb-8">{category.description}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.calculators.map((calculator, index) => (
                <CalculatorCard
                  key={index}
                  title={calculator.title}
                  description={calculator.description}
                  icon={calculator.icon}
                  link={calculator.link}
                  featured={calculator.featured}
                />
              ))}
            </div>
          </div>
        ))}

        <div className="bg-gray-100 rounded-lg p-8 mb-16">
          <h2 className="text-2xl font-bold mb-4">Australian Car Loan Terms Glossary</h2>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <h3 className="font-semibold">Principal Amount</h3>
              <p className="text-gray-600">The initial amount borrowed for your car loan, typically $10,000 to $50,000 for most Australian car purchases.</p>
            </div>
            <div>
              <h3 className="font-semibold">Comparison Rate</h3>
              <p className="text-gray-600">The true cost of the loan including interest rate and fees, as required by Australian law.</p>
            </div>
            <div>
              <h3 className="font-semibold">Secured Car Loan</h3>
              <p className="text-gray-600">A loan where your vehicle serves as collateral, offering lower interest rates (typically 4.99%-7.99% p.a. in Australia).</p>
            </div>
            <div>
              <h3 className="font-semibold">Balloon Payment</h3>
              <p className="text-gray-600">A lump sum payment due at the end of the loan term, popular in Australian car financing to reduce regular repayments.</p>
            </div>
            <div>
              <h3 className="font-semibold">Novated Lease</h3>
              <p className="text-gray-600">A popular Australian salary packaging arrangement where your employer makes repayments from your pre-tax salary.</p>
            </div>
            <div>
              <h3 className="font-semibold">Establishment Fee</h3>
              <p className="text-gray-600">A one-off fee charged by Australian lenders to set up your car loan, typically $150-$600.</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-8 rounded-lg border border-blue-100 mb-16">
          <h2 className="text-2xl font-bold mb-4">Current Australian Car Loan Market Trends</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start">
              <TrendingDown className="h-6 w-6 text-blue-600 mr-3 mt-1" />
              <div>
                <h3 className="text-lg font-semibold mb-1">Competitive Interest Rates</h3>
                <p className="text-gray-700">
                  Australian car loan interest rates currently range from 4.99% to 9.99% p.a. for secured loans, 
                  with major banks and online lenders competing for customers with good credit scores.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <CreditCard className="h-6 w-6 text-blue-600 mr-3 mt-1" />
              <div>
                <h3 className="text-lg font-semibold mb-1">Pre-Approval Options</h3>
                <p className="text-gray-700">
                  Many Australian lenders offer pre-approval services that allow you to shop with confidence knowing 
                  your financing is secured before visiting dealerships.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* New Australian States Section */}
        <div className="bg-white p-8 rounded-lg shadow-md mb-16">
          <div className="flex items-center mb-6">
            <MapPin className="h-7 w-7 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold">Car Loans Across Australian States</h2>
          </div>
          
          <p className="text-gray-700 mb-6">
            Car loan terms and conditions can vary between different states and territories across Australia. 
            Our calculator takes into account these regional differences to provide accurate estimates.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex items-center mb-3">
                <Building className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold">New South Wales & ACT</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Average interest rates: 5.49%-7.99%<br />
                Popular terms: 3-5 years<br />
                Common features: Fixed rates with early repayment options
              </p>
            </div>
            
            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex items-center mb-3">
                <Building className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold">Victoria</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Average interest rates: 5.29%-7.89%<br />
                Popular terms: 3-7 years<br />
                Common features: Competitive rates with flexible balloon options
              </p>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex items-center mb-3">
                <Building className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold">Queensland</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Average interest rates: 5.59%-8.10%<br />
                Popular terms: 4-6 years<br />
                Common features: Flood and severe weather protection options
              </p>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex items-center mb-3">
                <Building className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold">Western Australia</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Average interest rates: 5.69%-8.29%<br />
                Popular terms: 4-5 years<br />
                Common features: Mining industry-specific packages with 4WD options
              </p>
            </div>
            
            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex items-center mb-3">
                <Building className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold">South Australia</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Average interest rates: 5.45%-7.95%<br />
                Popular terms: 3-5 years<br />
                Common features: Green vehicle discounts for electric/hybrid cars
              </p>
            </div>
            
            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex items-center mb-3">
                <Building className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold">Tasmania & NT</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Average interest rates: 5.79%-8.49%<br />
                Popular terms: 3-5 years<br />
                Common features: Regional specific incentives and off-road vehicle packages
              </p>
            </div>
          </div>
          
          <div className="mt-6 bg-blue-50 p-4 rounded-md text-sm text-blue-700">
            <p>Our calculator uses the latest data from major Australian lenders across all states and territories. 
            Regional differences in interest rates, fees, and loan terms are automatically factored into your calculations.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;